import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

function ComposeEmail() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  const [userId, setUserId] = useState('');
  const [emailLists, setEmailLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingProgress, setSendingProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const uid = localStorage.getItem('userId');
    if (!uid) {
      setError("User not authenticated. Please log in again.");
      return;
    }
    setUserId(uid);
    fetchEmailLists(uid);
  }, []);

  const fetchEmailLists = async (uid) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/email-lists?user_id=${uid}`);
      const data = await res.json();
      if (data.success) {
        setEmailLists(data.email_lists);
      } else {
        setError(data.error || "Failed to fetch email lists.");
      }
    } catch (err) {
      setError("Error fetching email lists.");
    }
  };

  const fetchEmailsFromList = async (listId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/email-lists/${listId}/emails?user_id=${userId}`);
      const data = await res.json();
      if (data.success) {
        setRecipients(data.emails || []);
      } else {
        setError(data.error || "Failed to fetch emails.");
      }
    } catch (err) {
      setError("Error fetching email list.");
    }
  };

  const handleListChange = (e) => {
    const listId = e.target.value;
    setSelectedListId(listId);
    setRecipients([]);
    if (listId) fetchEmailsFromList(listId);
  };

  const handleGenerateAIContent = async () => {
    const prompt = window.prompt("Describe your email purpose:");
    if (!prompt) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/campaigns/generate-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      if (data.success) {
        setSubject(data.subject);
        setBody(data.body);
        setSuccess("AI-generated content loaded.");
      } else {
        setError(data.error || "Failed to generate content.");
      }
    } catch (err) {
      console.error(err);
      setError("AI content generation failed.");
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSendingProgress('');

    if (!subject.trim()) return setError("Subject is required.");
    if (!body.trim()) return setError("Body is required.");
    if (!recipients.length) return setError("Please select a valid email list.");
    if (!selectedListId) return setError("No email list selected.");
    if (!userId) return setError("User ID missing.");

    setLoading(true);
    setSendingProgress("Sending emails...");

    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('list_id', selectedListId);
    formData.append('subject', subject);
    formData.append('body', body);
    if (cc) formData.append('cc', cc);
    if (bcc) formData.append('bcc', bcc);
    if (attachment) {
      formData.append('attachment', attachment);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/campaigns/send`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(`Emails sent: ${data.total_sent}, Failed: ${data.total_failed}`);
        setSubject('');
        setBody('');
        setCc('');
        setBcc('');
        setAttachment(null);
        setRecipients([]);
        setSelectedListId('');
      } else {
        setError(data.error || "Failed to send campaign.");
      }
    } catch (err) {
      console.error(err);
      setError("Network error while sending campaign.");
    } finally {
      setLoading(false);
      setSendingProgress('');
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setRecipients(recipients.filter(email => email !== emailToRemove));
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto p-6">
        <h1 className="text-2xl font-semibold mb-4">Compose Email</h1>

        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
        {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{success}</div>}
        {sendingProgress && <div className="text-blue-700 mb-4">{sendingProgress}</div>}

        <form onSubmit={handleSendEmail} encType="multipart/form-data">
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">Select Email List</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={selectedListId}
              onChange={handleListChange}
            >
              <option value="">-- Select List --</option>
              {emailLists.map((list) => (
                <option key={list._id} value={list._id}>
                  {list.name} ({list.total_count} emails)
                </option>
              ))}
            </select>
          </div>

          {recipients.length > 0 && (
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Recipients:</label>
              <ul className="bg-white p-2 border rounded max-h-32 overflow-y-auto text-sm">
                {recipients.map((email) => (
                  <li key={email} className="flex justify-between items-center border-b px-2 py-1">
                    {email}
                    <button
                      type="button"
                      className="text-red-500 text-xs"
                      onClick={() => handleRemoveEmail(email)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Subject</label>
            <input
              type="text"
              className="w-full border rounded py-2 px-3"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
            />
          </div>

          {/* CC */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">CC</label>
            <input
              type="text"
              className="w-full border rounded py-2 px-3"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="Comma-separated emails"
            />
          </div>

          {/* BCC */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">BCC</label>
            <input
              type="text"
              className="w-full border rounded py-2 px-3"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="Comma-separated emails"
            />
          </div>

          {/* Attach PDF */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Attach PDF</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setAttachment(e.target.files[0])}
              className="w-full"
            />
            {attachment && (
              <div className="mt-2 flex items-center justify-between bg-gray-100 p-2 rounded">
                <span className="text-sm">{attachment.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  className="text-red-500 text-xs hover:underline ml-4"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Body + AI Button */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700">Body</label>
              <button
                type="button"
                onClick={handleGenerateAIContent}
                className="text-sm bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded"
              >
                ✨ Generate with AI
              </button>
            </div>
            <textarea
              className="w-full border rounded h-64 py-2 px-3"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your email content here..."
            />
          </div>

          <div className="flex justify-between items-center">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              {loading ? "Sending..." : "Send Campaign"}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="text-blue-500 hover:text-blue-700 text-sm"
            >
              ← Back to Dashboard
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ComposeEmail;
